import { useState } from "react"

import { 
  CustomModal, 
  Divider,   
  EmptyPlaceholder, 
  GuardianStudentDetails, 
} from "@components"

import { guardianFullname, studentFullname } from "@helpers"

import { 
  Button, 
  Checkbox, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  Spinner 
} from "@heroui/react"

import PropTypes from "prop-types"

const StudentGuardiansSelectorModal = ({
  guardians = [],
  handleClose,
  handleDownloadImportLogReport,
  handleDownloadMISGuardianEmailsReport,
  handleSave,
  isDownloadingImportLogReport,
  isDownloadingMISGuardianEmailsReport,
  isLoading = false,
  isSaving = false,
  selectedGuardianIds = [],
  show = false,
  student,
  title = "Select the guardians you would like to add to the student"
}) => {
  const [selectedIds, setSelectedIds] = useState([])
  const isDirty = selectedIds.length > 0
  
  const handleToggleGuardian = (guardianId, isSelected) => {
    (isSelected)
      ? setSelectedIds([...selectedIds, guardianId])
      : setSelectedIds(selectedIds.filter(id => id !== guardianId))
  }
  
  return (
    <CustomModal  
      isOpen={show}
      size="3xl"
      handleClose={() => handleClose()}
      scrollBehavior="inside"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>Guardians for {studentFullname(student)}</ModalHeader>
          
            <ModalBody>
              {isLoading && (
                <Spinner className="p-5" color="primary" label="Please wait loading guardians..." />
              )}

              {guardians.length > 0 && <p className="mt-2 -mb-2">{title}</p>}
                
              {!isLoading && guardians.length === 0 && (
                <EmptyPlaceholder 
                  classNames="mt-2" 
                  title="No Guardians Found" 
                  description="There are no guardians available to add for this student. This can be caused by duplicate or missing email addresses on your MIS. For more information download the relevant reports."
                >
                  <div className="flex">
                    <Button className="mr-2 font-semibold" onPress={handleDownloadImportLogReport} tabIndex={-1} isLoading={isDownloadingImportLogReport}>
                      Latest Import Log
                    </Button>
                    
                    <Button className="font-semibold" onPress={handleDownloadMISGuardianEmailsReport} tabIndex={-1} isLoading={isDownloadingMISGuardianEmailsReport}>
                      MIS Guardian Emails Report
                    </Button>
                  </div>
                </EmptyPlaceholder>                  
              )}

              {guardians.length > 0 && (
                <div className="flex flex-col">
                  {guardians.map(guardian => {
                    const isSelectedPreviously = selectedGuardianIds.includes(guardian.id)
                    const isSelected = isSelectedPreviously || selectedIds.includes(guardian.id)

                    return (
                      <div key={guardian.id}>
                        <div className="flex justify-between py-3 align-center">
                          <div className="flex gap-x-2">
                            <Checkbox 
                              key={guardian.id} 
                              color="secondary"
                              isSelected={isSelected} 
                              onValueChange={(isSelected) => handleToggleGuardian(guardian.id, isSelected)} 
                              isDisabled={isSelectedPreviously}
                            />
                            
                            <div>
                              <p className="text-lg font-semibold">{guardianFullname(guardian)}</p>
                              <p className="text-content2-foreground">{guardian.user.email}</p>
                            </div>
                          </div>

                          <GuardianStudentDetails contactSequence={guardian.pivot.contact_sequence} legalGuardian={guardian.pivot.legal_guardian} />
                        </div>

                        <Divider />
                      </div>
                    )
                  })}
                </div>
              )}
            </ModalBody>

            <ModalFooter>
              <div className="flex justify-between w-full">
                <Button onPress={() => handleClose()} tabIndex={-1}>Cancel</Button>
                <Button color="secondary" onPress={() => handleSave(student.id, selectedIds)} tabIndex={-1} isDisabled={!isDirty} isLoading={isSaving}>Add Guardian(s)</Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </CustomModal>
  )
}

PropTypes.StudentGuardiansSelectorModal = {
  guardians: PropTypes.array.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleDownloadImportLogReport: PropTypes.func.isRequired,
  handleDownloadMISGuardianEmailsReport: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  isDownloadingImportLogReport: PropTypes.bool,
  isDownloadingMISGuardianEmailsReport: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSaving: PropTypes.bool,
  selectedGuardianIds: PropTypes.array,
  show: PropTypes.bool,
  student: PropTypes.object.isRequired,
  title: PropTypes.string
}

export default StudentGuardiansSelectorModal